'use client';
import { useEffect, useRef } from 'react';

import Text from '@/components/common/Text/Text';
import { ApplicationErrorItem, isConsentOnly } from './applicationErrors';
import './ApplicationErrorSummary.style.scss';

type ApplicationErrorSummaryProps = {
  items: ApplicationErrorItem[];
};

function focusTarget(selector: string) {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) {
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Stripe mounts and section wrappers are not focusable — scrolling is enough.
  if (typeof el.focus === 'function') {
    el.focus({ preventScroll: true });
  }
}

/**
 * The submit-time error summary shown when the member tries to Continue with
 * missing/invalid fields. Replaces a permanently-disabled Continue button (an
 * a11y anti-pattern that never says *why*). It is a focusable `role="alert"` so
 * screen readers announce it, and each entry scrolls/focuses its field.
 */
function ApplicationErrorSummary(props: ApplicationErrorSummaryProps) {
  const { items } = props;
  const ref = useRef<HTMLDivElement>(null);

  // Move focus to the summary when it appears (or when its contents change) so
  // keyboard and SR users are taken straight to it.
  useEffect(() => {
    if (items.length > 0) {
      ref.current?.focus();
    }
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  if (isConsentOnly(items)) {
    return (
      <div
        ref={ref}
        role='alert'
        tabIndex={-1}
        className='ApplicationErrorSummary-consent'
        data-testid='application-error-summary'
      >
        <Text
          variant='text-l'
          className='error'
        >
          You must agree to all terms to continue
        </Text>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role='alert'
      tabIndex={-1}
      className='ApplicationErrorSummary'
      data-testid='application-error-summary'
    >
      <Text
        variant='title-xxs-700'
        className='ApplicationErrorSummary-title'
      >
        Please complete the following before continuing:
      </Text>
      <ul className='ApplicationErrorSummary-list'>
        {items.map((item) => (
          <li key={item.label}>
            <button
              type='button'
              className='ApplicationErrorSummary-link cursor'
              onClick={() => focusTarget(item.selector)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ApplicationErrorSummary;
